/**
 * Analytics and tracking utilities
 * Logs important user actions and system events
 */

export interface AnalyticsEvent {
  name: string;
  properties?: Record<string, any>;
  timestamp?: number;
}

/**
 * Track a user event
 */
export function trackEvent(name: string, properties?: Record<string, any>): void {
  const event: AnalyticsEvent = {
    name,
    properties,
    timestamp: Date.now()
  };
  
  // Log to console in development
  if (import.meta.env.DEV) {
    console.log('[Analytics]', event);
  }
  
  // Store in localStorage for now (can integrate with analytics service later)
  const events = JSON.parse(localStorage.getItem('analytics_events') || '[]');
  events.push(event);
  
  // Keep last 100 events
  if (events.length > 100) {
    events.shift();
  }
  
  localStorage.setItem('analytics_events', JSON.stringify(events));
}

/**
 * Track page view
 */
export function trackPageView(pageName: string): void {
  trackEvent('page_view', { page: pageName });
}

/**
 * Track user signup
 */
export function trackSignup(method: string): void {
  trackEvent('user_signup', { method });
}

/**
 * Track ping interaction
 */
export function trackPingOpened(campaignId: string, reward: number): void {
  trackEvent('ping_opened', { campaignId, reward });
}

/**
 * Track payout request
 */
export function trackPayoutRequest(amount: number, method: string): void {
  trackEvent('payout_request', { amount, method });
}

/**
 * Track referral
 */
export function trackReferral(referralCode: string): void {
  trackEvent('referral_used', { referralCode });
}

/**
 * Track streak milestone
 */
export function trackStreakMilestone(days: number): void {
  trackEvent('streak_milestone', { days });
}

/**
 * Track campaign creation (advertiser)
 */
export function trackCampaignCreated(budget: number, targetingUsed: string[]): void {
  trackEvent('campaign_created', { budget, targetingUsed });
}

/**
 * Get analytics summary
 */
export function getAnalyticsSummary(): {
  totalEvents: number;
  eventsByType: Record<string, number>;
  recentEvents: AnalyticsEvent[];
} {
  const events: AnalyticsEvent[] = JSON.parse(
    localStorage.getItem('analytics_events') || '[]'
  );
  
  const eventsByType: Record<string, number> = {};
  events.forEach(event => {
    eventsByType[event.name] = (eventsByType[event.name] || 0) + 1;
  });
  
  return {
    totalEvents: events.length,
    eventsByType,
    recentEvents: events.slice(-10)
  };
}

/**
 * Clear analytics data
 */
export function clearAnalytics(): void {
  localStorage.removeItem('analytics_events');
}
